<template>
  <div class="ativacao-page">
    <div class="container">
      <div class="row justify-center items-center" style="min-height: 100vh;">
        <div class="col-12 col-md-6 col-lg-5">
          <q-card flat bordered class="ativacao-card">
            <q-card-section class="text-center q-pt-xl q-pb-lg">
              <!-- Ícone animado -->
              <div class="icon-wrapper q-mb-lg">
                <q-spinner-hourglass
                  v-if="processando"
                  color="primary"
                  size="80px"
                />
                <q-icon
                  v-else-if="ativado"
                  name="check_circle"
                  color="green"
                  size="80px"
                  class="animated-icon"
                />
                <q-icon
                  v-else-if="erro"
                  name="error"
                  color="red"
                  size="80px"
                  class="animated-icon"
                />
              </div>

              <!-- Título -->
              <h4 class="text-h4 text-weight-bold text-grey-9 q-mb-md">
                <span v-if="processando">Ativando sua conta...</span>
                <span v-else-if="ativado">Conta Ativada!</span>
                <span v-else-if="erro">Erro na Ativação</span>
              </h4>

              <!-- Mensagem -->
              <p class="text-body1 text-grey-7 q-mb-lg">
                <span v-if="processando">
                  Por favor, aguarde enquanto processamos sua ativação.
                </span>
                <span v-else-if="ativado">
                  Sua conta foi ativada com sucesso! Você já pode fazer login.
                </span>
                <span v-else-if="erro">
                  {{ mensagemErro }}
                </span>
              </p>

              <!-- Progresso -->
              <q-linear-progress
                v-if="processando"
                indeterminate
                color="primary"
                class="q-mb-lg"
              />

              <!-- Botões de Ação -->
              <div v-if="!processando" class="q-gutter-sm">
                <q-btn
                  v-if="ativado"
                  unelevated
                  color="primary"
                  label="Ir para Login"
                  icon="login"
                  size="lg"
                  class="full-width"
                  @click="irParaLogin"
                />

                <q-btn
                  v-if="erro"
                  unelevated
                  color="primary"
                  label="Tentar Novamente"
                  icon="refresh"
                  size="lg"
                  class="full-width"
                  @click="tentarNovamente"
                />

                <q-btn
                  v-if="erro"
                  outline
                  color="grey-7"
                  label="Voltar ao Início"
                  icon="home"
                  size="lg"
                  class="full-width"
                  @click="voltarInicio"
                />
              </div>
            </q-card-section>

            <!-- Informações Adicionais -->
            <q-separator v-if="!processando" />

            <q-card-section v-if="!processando" class="bg-grey-1">
              <div class="text-center">
                <q-icon name="info" color="blue-7" size="20px" />
                <span class="text-caption text-grey-7 q-ml-xs">
                  <span v-if="ativado">
                    Use suas credenciais para acessar sua conta
                  </span>
                  <span v-else-if="erro">
                    Precisa de ajuda? Entre em contato com o suporte
                  </span>
                </span>
              </div>
            </q-card-section>
          </q-card>

          <!-- Link de Suporte -->
          <div class="text-center q-mt-md">
            <q-btn
              flat
              dense
              color="grey-7"
              label="Suporte"
              icon="help"
              size="sm"
              @click="abrirSuporte"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import Swal from 'sweetalert2';
import store from '@/vuex/store';

const router = useRouter();
const route = useRoute();

// Estados
const processando = ref(true);
const ativado = ref(false);
const erro = ref(false);
const mensagemErro = ref('');

// Token da URL (pode vir como prop ou route param)
const token = ref(route.params.token || route.query.token || '');

// Função para ativar a conta
const ativarConta = async () => {
  processando.value = true;
  ativado.value = false;
  erro.value = false;

  try {
    // Validar se o token existe
    if (!token.value) {
      throw new Error('Token de ativação não fornecido.');
    }
    console.log('Token de ativação:', token.value);
    await store.dispatch('auth/activateAccount', { token: token.value });

    // Sucesso
    processando.value = false;
    ativado.value = true;

    // Mostrar SweetAlert de sucesso
    Swal.fire({
      title: 'Conta Ativada!',
      text: 'Sua conta foi ativada com sucesso. Você será redirecionado para o login.',
      icon: 'success',
      confirmButtonText: 'Ir para Login',
      confirmButtonColor: '#1976d2',
      timer: 5000,
      timerProgressBar: true,
      allowOutsideClick: false,
      allowEscapeKey: false
    }).then((result) => {
      if (result.isConfirmed || result.isDismissed) {
        irParaLogin();
      }
    });

  } catch (error) {
    // Erro
    processando.value = false;
    erro.value = true;
    mensagemErro.value = error.message || 'Ocorreu um erro ao ativar sua conta. Por favor, tente novamente.';

    // Mostrar SweetAlert de erro
    Swal.fire({
      title: 'Erro na Ativação',
      text: mensagemErro.value,
      icon: 'error',
      confirmButtonText: 'Tentar Novamente',
      confirmButtonColor: '#1976d2',
      showCancelButton: true,
      cancelButtonText: 'Voltar ao Início',
      cancelButtonColor: '#757575',
      allowOutsideClick: false
    }).then((result) => {
      if (result.isConfirmed) {
        tentarNovamente();
      } else if (result.isDismissed) {
        voltarInicio();
      }
    });
  }
};

// Ir para login
const irParaLogin = () => {
  router.push('/');
};

// Tentar novamente
const tentarNovamente = () => {
  ativarConta();
};

// Voltar ao início
const voltarInicio = () => {
  router.push('/');
};

// Abrir suporte
const abrirSuporte = () => {
  Swal.fire({
    title: 'Suporte',
    html: `
      <p>Entre em contato conosco:</p>
      <p><strong>Email:</strong> suporte@goldenkey.co.mz</p>
      <p><strong>WhatsApp:</strong>+258 82 701 6716</p>
    `,
    icon: 'info',
    confirmButtonText: 'Fechar',
    confirmButtonColor: '#1976d2'
  });
};

// Ativar conta ao montar componente
onMounted(() => {
  ativarConta();
});
</script>

<style scoped>
.ativacao-page {
  min-height: 100vh;
  padding: 20px;
}

.container {
  max-width: 1400px;
  margin: 0 auto;
}

.ativacao-card {
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  background: white;
}

.icon-wrapper {
  animation: fadeIn 0.5s ease-in;
}

.animated-icon {
  animation: scaleIn 0.5s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes scaleIn {
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
}

/* Animação de pulso para ícone de sucesso */
.animated-icon {
  animation: scaleIn 0.5s ease-out, pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

@media (max-width: 599px) {
  .text-h4 {
    font-size: 1.5rem;
  }
  
  .icon-wrapper i,
  .icon-wrapper .q-spinner {
    font-size: 60px !important;
    width: 60px !important;
    height: 60px !important;
  }
}
</style>